import { ContentService } from '@blk/cwp-ng-framework';
import { AbstractRenderer, Alignment, BoxModelConstraint, BusinessComponent, BusinessComponentOptions, CellPanel, ColumnComposition, ColumnPanel, PageBreak, RowPanel, StaticParagraphComponent } from '@blk/simba';
import { InjectConfig, SharedConfigKey } from '@ddt-web/config-layer';
import { ComponentData, TabData, TabUtil } from '@ddt-web/data-service';
import { ReportUtils } from '@ddt-web/pdf-utils';
import * as get from 'lodash/get';

import { HeadingComponent, HeadingLevel } from '../../heading/heading-component';
import { HeadingWithDateComponent } from '../../heading/heading-with-date-component';
import { DefaultColumnComposition } from '../../helpers/column-compositions/default-column-composition';
import { TableUtils } from '../../helpers/utils/table-utils';
import { PerformanceTableUtil } from '../util/performance-table.util';
import { PerformanceVisibilityUtil } from '../util/performance-visibility.util';

export class HypotheticalPerformanceComponent extends BusinessComponent {
	@InjectConfig(SharedConfigKey.EnableCustomFee) private readonly isCustomFeeEnabled: boolean = false;

	// Table
	private hypoGrossPerfTableTabData: TabData;
	private hypoNetCustomPerfTableTabData: TabData;

	// we need to provide exact proportions when SEC enabled for 11 columns as the dynamic way does not fit properly 11 columns with long names
	private readonly SEC_COLUMN_IDS = ['sec10Year', 'secSinceInception', 'secInceptionDate'];
	private readonly TABLE_CUSTOM_PROPORTIONS_FOR_SEC: number[] = [9, 12, 8, 8.5, 8.5, 8.5, 8.5, 8.5, 8.5, 10, 10];
	private readonly CELL_CUSTOM_BOX_MODEL_CONSTRAINT_FOR_SEC_GROSS: Partial<BoxModelConstraint> = { paddingLeft: 3, paddingRight: 3 };
	private readonly CELL_CUSTOM_BOX_MODEL_CONSTRAINT_FOR_SEC_NET: Partial<BoxModelConstraint> = { paddingTop: 11, paddingLeft: 3, paddingRight: 3 };

	constructor(
		name: string,
		public dataComponentId: string,
		public currencyISO?: string,
		public disclaimerTextClasses?: string[],
		public displayPerformanceHeader = true,
		public shouldStartFromNewPage: boolean = false,
		public yAxisLabel: string = null,
		public customAdvisoryFee: number = null,
		public visible: boolean = true
	) {
		super(name, 'BusinessComponent', BusinessComponentOptions.merge({ visible: visible, componentId: dataComponentId }));
	}

	public isComponentDataVisible(componentData: ComponentData): boolean {
		return PerformanceVisibilityUtil.isPerformanceComponentDataVisible(componentData, this.isCustomFeeEnabled, this.customAdvisoryFee);
	}

	public buildComponent(componentData: ComponentData, contentSvc: ContentService): AbstractRenderer<SVGGElement>[] {
		this.hypoGrossPerfTableTabData = componentData && TabUtil.getVisibleTabData(componentData.getTabById('hypo-performance'));
		this.hypoNetCustomPerfTableTabData = componentData && TabUtil.getVisibleTabData(componentData.getTabById('hypo-net-custom-performance'));

		const panels: AbstractRenderer<SVGGElement>[] = [];

		if (this.isComponentDataVisible(componentData)) {
			if (this.shouldStartFromNewPage) {
				panels.push(new PageBreak());
			}
			// add section header
			if (this.displayPerformanceHeader) {
				panels.push(new HeadingComponent('PERFORMANCE_H1', 'PERFORMANCE', HeadingLevel.H1, { paddingTop: 0, paddingBottom: 8 }));
			}

			const dcrKeyPrefix = ReportUtils.createDcrKeyPrefixForPdf(this.name);
			const asOfDate = get(componentData.getTabById('hypo-perf-as-of-date'), 'tableData[0].cells[0][0].formattedValue');

			if (this.hypoGrossPerfTableTabData) {
				this.updatePanelsWithPerformanceTables(panels, dcrKeyPrefix, asOfDate, contentSvc);
			}
		}

		return panels;
	}

	private updatePanelsWithPerformanceTables(panels: Array<AbstractRenderer<SVGGElement>>, dcrKeyPrefix: string, asOfDate: string, contentSvc: ContentService): void {
		// add hypo table header
		if (this.displayPerformanceHeader) {
			const tabWithDefaultTitle = this.hypoGrossPerfTableTabData;
			panels.push(
				new HeadingWithDateComponent().buildHeader(
					'Hypo Performance Header', 'PDF_HYPO_PERF_TABLE_H2', get(tabWithDefaultTitle, 'config.title.label', '').toString(), asOfDate
				)
			);
		}

		const shouldIncludeAnnualizedDisclaimer = contentSvc.getBoolean('SHOULD_INCLUDE_ANNUALIZED_HYPO_DISCLAIMER_PDF', true);

		// we need to provide exact proportions when SEC enabled for 11 columns (10 headers + name) as the dynamic way does not fit properly longer column names
		const headersX = this.hypoGrossPerfTableTabData.tableData[0].headersX.filter(header => header.config.visible);
		const isSecColumnsProvided = this.SEC_COLUMN_IDS.every(id => headersX.find(header => header.config.id === id)) && headersX.length === 10;

		if (this.hypoNetCustomPerfTableTabData) {
			const performanceTableDataArray = [this.hypoGrossPerfTableTabData, this.hypoNetCustomPerfTableTabData];
			panels.push(
				...PerformanceTableUtil.buildCustomFeePerformanceTable(
					performanceTableDataArray,
					dcrKeyPrefix,
					shouldIncludeAnnualizedDisclaimer,
					this.customAdvisoryFee,
					contentSvc,
					isSecColumnsProvided ? this.TABLE_CUSTOM_PROPORTIONS_FOR_SEC : null,
					isSecColumnsProvided ? this.CELL_CUSTOM_BOX_MODEL_CONSTRAINT_FOR_SEC_GROSS : null,
					isSecColumnsProvided ? this.CELL_CUSTOM_BOX_MODEL_CONSTRAINT_FOR_SEC_NET : null
				)
			);
		} else {
			panels.push(...this.buildHypoPerfTablePanelItems(this.hypoGrossPerfTableTabData, dcrKeyPrefix, shouldIncludeAnnualizedDisclaimer));
		}
	}

	private buildHypoPerfTablePanelItems(tabData: TabData, dcrKeyPrefix: string, shouldIncludeAnnualizedDisclaimer: boolean): Array<AbstractRenderer<SVGGElement>> {
		const panels: AbstractRenderer<SVGGElement>[] = [];
		const tableDefinitions = new DefaultColumnComposition().getColumnCompositions(
			tabData,
			[40, ...TableUtils.calculateColumnProportions(tabData.tableData[0].headersX.length, 60)],
			true
		);

		const headerRow: RowPanel = new RowPanel(
			'Hypo Table Header Row',
			tableDefinitions.headers.map((config: ColumnComposition) => config.renderer.render()),
			{ marginBottom: 5, paddingTop: 5, borderBottom: 0.25, borderBottomColor: '#000' },
			Alignment.Horizontal.Left,
			Alignment.Vertical.Bottom
		);

		const rows: Array<RowPanel> = [];
		tableDefinitions.body.forEach((configs: Array<ColumnComposition>, index: number) => {
			const rowRenderer = configs.map((config: ColumnComposition) => config.renderer.render());
			const boxModelConstraint =
				index !== tableDefinitions.body.length - 1 ? { marginBottom: 5, paddingTop: 5, borderBottom: 0.25, borderBottomColor: '#616161' } : { paddingTop: 5 };
			rows.push(new RowPanel('bodyRow', rowRenderer, boxModelConstraint, Alignment.Horizontal.Left, Alignment.Vertical.Middle));
		});
		const bodyRow: RowPanel = new RowPanel('Hypo Table Body Row', [new CellPanel(rows, { proportion: 100 }, Alignment.Horizontal.Left)], { paddingBottom: 18 });

		panels.push(headerRow, bodyRow);

		if (shouldIncludeAnnualizedDisclaimer) {
			panels.push(new StaticParagraphComponent(dcrKeyPrefix + 'TABLE_P2', '* Annualized', {}, { paddingBottom: 5 }, ['text-condensed']));
		}

		return [new ColumnPanel('Hypothetical Portfolio Performance Section', panels, { marginBottom: 10 }, Alignment.Horizontal.Left)];
	}
}
